/**
 * Codec for the Metrics Explore URL ↔ state round-trip.
 *
 * The Explore page stores its entire visual state in the URL so a
 * shared link reproduces the exact chart. The series array is
 * base64-encoded JSON to keep the URL parseable when 5+ series are
 * stacked (each series can carry up to 7 facets — base64 collapses
 * the comma + quote noise).
 *
 * The schema is intentionally tolerant: unknown fields are dropped,
 * invalid encodings fall through to a default seeded from the route
 * context (the metric tile the user clicked from). Bad URLs should
 * never render an error page.
 *
 * This codec is also the source of truth for the eventual
 * `metric_views.config jsonb` column (when saved-views land). Don't
 * diverge the shape — `decodeSeries(encodeSeries(state)) === state`
 * is the contract that lets saving be a one-liner.
 */
import {
  BUCKETS,
  type Bucket,
  EXPLORE_METRIC_KEYS,
  type ExploreMetricKey,
  PERCENTILES,
  type Percentile,
  RANGES,
  type Range,
  type Series,
  SeriesSchema,
  VISUALISATIONS,
  type Visualisation,
} from '@clickweave/shared';

/** Stable nano-style id generator for new series. URL-safe alphabet. */
const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
export function newSeriesId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let id = 's_';
  for (const b of bytes) id += ID_ALPHABET[b % ID_ALPHABET.length];
  return id;
}

/** URL-safe base64 (RFC 4648 §5) — `+/=` → `-_` and no padding. */
function b64UrlEncode(s: string): string {
  // btoa handles ascii; we pass JSON which is ascii-only.
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64UrlDecode(s: string): string {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const fill = '='.repeat((4 - (padded.length % 4)) % 4);
  return atob(padded + fill);
}

export interface ExploreUrlState {
  series: Series[];
  range: Range;
  bucket: Bucket;
}

export function encodeSeries(series: Series[]): string {
  return b64UrlEncode(JSON.stringify(series));
}

export function decodeSeries(encoded: string): Series[] | null {
  try {
    const json = JSON.parse(b64UrlDecode(encoded));
    if (!Array.isArray(json)) return null;
    const out: Series[] = [];
    for (const candidate of json) {
      const parsed = SeriesSchema.safeParse(candidate);
      if (parsed.success) out.push(parsed.data);
      // Drop invalid entries silently — a corrupt series in a shared
      // URL shouldn't poison the whole view.
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Build a default single-series state from the navigation context (the
 * MetricTile the user clicked). Used both as the initial fill when
 * `?series=` is absent and as a fallback when decode fails.
 */
export function defaultStateFor(context: {
  metric: ExploreMetricKey;
  site_uuid: string;
  page_uuid?: string;
  device?: 'mobile' | 'desktop';
  region?: string;
}): ExploreUrlState {
  return {
    series: [
      {
        id: newSeriesId(),
        metric: context.metric,
        site_uuid: context.site_uuid,
        ...(context.page_uuid ? { page_uuid: context.page_uuid } : {}),
        ...(context.device ? { device: context.device } : {}),
        ...(context.region ? { region: context.region } : {}),
        percentile: 'p75' as Percentile,
      },
    ],
    range: '30d',
    bucket: '1d',
  };
}

/** Coerce a search-param value to a known Range or fall back. */
export function parseRange(raw: unknown, fallback: Range = '30d'): Range {
  return (RANGES as readonly string[]).includes(raw as string) ? (raw as Range) : fallback;
}

export function parseBucket(raw: unknown, fallback: Bucket = '1d'): Bucket {
  return (BUCKETS as readonly string[]).includes(raw as string) ? (raw as Bucket) : fallback;
}

export function isExploreMetricKey(s: unknown): s is ExploreMetricKey {
  return (EXPLORE_METRIC_KEYS as readonly string[]).includes(s as string);
}

export function isPercentile(s: unknown): s is Percentile {
  return (PERCENTILES as readonly string[]).includes(s as string);
}

export function isVisualisation(s: unknown): s is Visualisation {
  return (VISUALISATIONS as readonly string[]).includes(s as string);
}
